import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAppContext, View } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import { PrivacyBadge } from '../ui/PrivacyBadge';
import { useTranslation } from 'react-i18next';
import { triggerHaptic } from '../../utils/haptics';

export const MobileDrawer: React.FC = () => {
    const { isMenuOpen, setIsMenuOpen, currentGroup, goToPage, view, db } = useAppContext();
    const { user, userData } = useAuth();
    const { t } = useTranslation();
    const [legalOpen, setLegalOpen] = useState(false);
    const navigate = useNavigate();
    const wl = db.globalConfig?.whiteLabel;

    // Use userData from Auth first (most reliable), then fall back to group players
    const currentPlayer = userData || currentGroup?.players?.find(p => p.id === user?.uid) ||
        db.groups.flatMap(g => g.players || []).find(p => p.id === user?.uid);

    const handleMenuItemClick = (targetView: View, data?: any) => {
        triggerHaptic();
        setIsMenuOpen(false);
        goToPage(targetView, data);
    };

    if (!user) return null;

    return (
        <div className={`fixed inset-0 z-[100] pointer-events-none ${isMenuOpen ? 'pointer-events-auto' : ''}`}>
            {/* Backdrop */}
            <div
                className={`absolute inset-0 bg-black/50 transition-opacity duration-300 ${isMenuOpen ? 'opacity-100' : 'opacity-0'}`}
                onClick={() => setIsMenuOpen(false)}
            />

            {/* Menu Panel */}
            <div className={`absolute top-0 left-0 h-full w-80 max-w-[85vw] bg-background shadow-2xl transform transition-transform duration-300 ease-out overflow-y-auto ${isMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
                <div className="p-6 safe-area-pt space-y-6">
                    {/* Header with gradient */}
                    <div className="flex items-center justify-between mb-6 pb-4 border-b-2 border-primary/20">
                        <div>
                            <h2 className="text-2xl font-black text-primary tracking-tight">{t('sidebar.menu', 'MENU')}</h2>
                        </div>
                        <button
                            onClick={() => setIsMenuOpen(false)}
                            className="p-3 min-w-[48px] min-h-[48px] flex items-center justify-center rounded-full hover:bg-surface-2 transition-colors"
                        >
                            <span className="material-symbols-outlined text-primary">close</span>
                        </button>
                    </div>

                    {/* Accueil (Hub) */}
                    <button
                        onClick={() => handleMenuItemClick(View.HUB)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg bg-surface-2 hover:bg-surface-3 transition-colors"
                    >
                        <span className="material-symbols-outlined text-primary text-xl">home</span>
                        <span className="font-semibold text-text-primary text-sm">{t('sidebar.home', 'Accueil')}</span>
                    </button>

                    {/* Les Groupes */}
                    <button
                        onClick={() => handleMenuItemClick(View.GROUP_SELECT)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg bg-surface-2 hover:bg-surface-3 transition-colors"
                    >
                        <span className="material-symbols-outlined text-primary text-xl">groups</span>
                        <span className="font-semibold text-text-primary text-sm">{t('sidebar.groups', 'Les Groupes')}</span>
                    </button>

                    {/* Groupe Sélectionné Section */}
                    {currentGroup && (view === View.HOME || view === View.TOURNAMENTS || view === View.TOURNAMENT_DETAIL || view === View.PLAYERS || view === View.PLAYER_DETAIL || view === View.H2H || view === View.CREATE_TOURNAMENT || view === View.CREATE_TEAM || view === View.CREATE_MATCH || view === View.BRACKET || view === View.KOTC_DETAIL || view === View.MATCHMAKING) && (
                        <div className="border-t border-glass-border pt-4 space-y-2">
                            <div className="flex items-center gap-2 px-2">
                                <h3 className="text-xs font-bold text-primary uppercase tracking-wide">
                                    {currentGroup.name}
                                </h3>
                                {currentGroup.id !== 'official_padel_brothers' && (
                                    <PrivacyBadge
                                        mode={currentGroup.privacyMode}
                                        compact
                                        className="w-4 h-4 text-[10px]"
                                        isMember={(currentGroup.players || []).some(p => p.id === user?.uid) || (currentGroup.members || []).includes(user?.uid || '')}
                                    />
                                )}
                            </div>

                            <button
                                onClick={() => handleMenuItemClick(View.TOURNAMENTS)}
                                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-surface-2 transition-colors"
                            >
                                <span className="material-symbols-outlined text-text-secondary text-lg">emoji_events</span>
                                <span className="text-text-primary text-sm">{t('sidebar.tournaments', 'Les Tournois')}</span>
                            </button>

                            <button
                                onClick={() => handleMenuItemClick(View.MATCHMAKING)}
                                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-surface-2 transition-colors"
                            >
                                <span className="material-symbols-outlined text-text-secondary text-lg">handshake</span>
                                <span className="text-text-primary text-sm">{t('sidebar.matchmaking', 'MatchMaking')}</span>
                            </button>

                            <button
                                onClick={() => handleMenuItemClick(View.PLAYERS)}
                                className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-surface-2 transition-colors"
                            >
                                <span className="material-symbols-outlined text-text-secondary text-lg">leaderboard</span>
                                <span className="text-text-primary text-sm">{t('sidebar.ranking', 'Classement')}</span>
                            </button>
                        </div>
                    )}

                    {/* Les Clubs */}
                    <button
                        onClick={() => handleMenuItemClick(View.CLUBS)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg bg-surface-2 hover:bg-surface-3 transition-colors"
                    >
                        <span className="material-symbols-outlined text-primary text-xl">sports_tennis</span>
                        <span className="font-semibold text-text-primary text-sm">{t('sidebar.clubs', 'Les Clubs')}</span>
                    </button>

                    {/* Règles */}
                    <button
                        onClick={() => handleMenuItemClick(View.RULES)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg bg-surface-2 hover:bg-surface-3 transition-colors"
                    >
                        <span className="material-symbols-outlined text-primary text-xl">article</span>
                        <span className="font-semibold text-text-primary text-sm">{t('sidebar.rules', 'Règles')}</span>
                    </button>

                    {/* Mon Profil */}
                    <button
                        onClick={() => handleMenuItemClick(View.PROFILE)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg bg-surface-2 hover:bg-surface-3 transition-colors mt-4"
                    >
                        <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center overflow-hidden border-2 border-primary">
                            {(() => {
                                const photoUrl = currentPlayer?.photoURL || currentPlayer?.avatar;
                                const isUrl = photoUrl && (photoUrl.startsWith('http') || photoUrl.startsWith('data:image') || photoUrl.startsWith('/'));

                                if (isUrl) {
                                    return <img src={photoUrl} className="w-full h-full object-cover" alt="Profile" />;
                                } else if (currentPlayer?.avatar && !currentPlayer.avatar.startsWith('http') && !currentPlayer.avatar.startsWith('/') && !currentPlayer.avatar.startsWith('data:')) {
                                    return <span className="text-sm">{currentPlayer.avatar}</span>;
                                } else {
                                    return <span className="text-sm font-bold">{currentPlayer?.pseudo?.charAt(0) || user?.displayName?.charAt(0) || '👤'}</span>;
                                }
                            })()}
                        </div>
                        <span className="font-semibold text-text-primary text-sm">{t('sidebar.myProfile', 'Mon Profil')}</span>
                    </button>

                    {/* Arbitrage/Scoreboard */}
                    <button
                        onClick={() => handleMenuItemClick(View.SCORE)}
                        className="w-full flex items-center gap-3 p-3 rounded-lg bg-gradient-to-r from-primary to-accent hover:brightness-110 transition-all shadow-lg active:scale-95 group mt-4 relative overflow-hidden"
                    >
                        <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700 ease-in-out" />
                        <span className="material-symbols-outlined text-white text-xl drop-shadow-md">scoreboard</span>
                        <span className="font-bold text-white text-sm tracking-wide drop-shadow-md">{t('sidebar.moderateMatch', 'MODERER UN MATCH')}</span>
                    </button>

                    {/* Nous contacter */}
                    <button
                        onClick={() => { setIsMenuOpen(false); navigate('/contact'); }}
                        className="w-full flex items-center gap-3 p-3 rounded-lg bg-surface-2 hover:bg-surface-3 transition-colors"
                    >
                        <span className="material-symbols-outlined text-primary text-xl">mail</span>
                        <span className="font-semibold text-text-primary text-sm">{t('sidebar.contactUs', 'Nous contacter')}</span>
                    </button>

                    {/* Legal Links Section */}
                    <div className="pt-4 mt-4">
                        <button
                            onClick={() => setLegalOpen(!legalOpen)}
                            className="w-full flex items-center justify-between px-3 py-2 text-xs font-bold text-text-secondary uppercase tracking-wider hover:text-primary transition-colors"
                        >
                            <span className="flex items-center gap-2">
                                <span className="material-symbols-outlined text-sm">gavel</span>
                                {t('sidebar.legalInfo', 'Informations légales')}
                            </span>
                            <span className={`material-symbols-outlined text-sm transition-transform ${legalOpen ? 'rotate-180' : ''}`}>
                                expand_more
                            </span>
                        </button>
                        {legalOpen && (
                            <div className="mt-1 space-y-1">
                                <Link to="/legal?tab=terms" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-surface-2 transition-colors group">
                                    <span className="material-symbols-outlined text-text-secondary group-hover:text-primary transition-colors">description</span>
                                    <span className="text-sm text-text-primary">{t('sidebar.termsOfUse', "Conditions d'utilisation")}</span>
                                </Link>
                                <Link to="/legal?tab=privacy" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-surface-2 transition-colors group">
                                    <span className="material-symbols-outlined text-text-secondary group-hover:text-primary transition-colors">shield</span>
                                    <span className="text-sm text-text-primary">{t('sidebar.privacyPolicy', 'Politique de confidentialité')}</span>
                                </Link>
                                <Link to="/legal?tab=legal" onClick={() => setIsMenuOpen(false)} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-surface-2 transition-colors group">
                                    <span className="material-symbols-outlined text-text-secondary group-hover:text-primary transition-colors">gavel</span>
                                    <span className="text-sm text-text-primary">{t('sidebar.legalNotices', 'Mentions légales')}</span>
                                </Link>
                            </div>
                        )}
                    </div>

                    {/* Footer info */}
                    <div className="pt-4 mt-4 border-t border-glass-border text-center">
                        <p className="text-xs text-text-secondary">© {new Date().getFullYear()} {wl?.enabled ? `${wl.appName.toUpperCase()}${wl.companyName ? ` By ${wl.companyName}` : ''}` : 'PADEL BROTHERS By Pro Art'}</p>
                    </div>
                </div>
            </div>
        </div>
    );
};
